import Component from 'inferno-component';
import * as d3 from 'd3';
import './BarChart.css';

const barThickness = 5;

const initialData = [
  { name: '', value: 0 },
  { name: ' ', value: 0 },
  { name: '  ', value: 0 },
  { name: '   ', value: 0 },
  { name: '    ', value: 0 }
];

const margin = { top: 80, right: 20, bottom: 30, left: 40 };

const x = (width) => {
  return d3.scaleBand().range([0, width]).padding(0.1);
};

const y = (height) => {
  return d3.scaleLinear().range([height, 0]);
};

class BarChart extends Component {
  constructor(props) {
    super(props);
    this.state = { selected: {}, currentSkill: {} };
  }

  componentDidMount() {
    this.initChart(initialData);
    this.updateChart(initialData, 0);
    window.addEventListener('resize', this.resize);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.arePropsSame(this.props.data, nextProps.data)) {
      this.updateChart(nextProps.data);
      this.setState({ data: nextProps.data });
    }
    if (nextProps.currentSkill && (!this.props.currentSkill || this.props.currentSkill.name !== nextProps.currentSkill.name)) {
      this.setState({ currentSkill: nextProps.currentSkill });
      this.setCurrentInBars(nextProps.currentSkill.name);
    }
  }

  arePropsSame(a, b) {
    if (a.length === 0 && b.length !== 0) {
      return false;
    } else {
      if (a.length === b.length) {
        for (let i in a) {
          let itemA = a[i];
          let itemB = b[i];
          if (itemA.name !== itemB.name || itemA.value !== itemB.value || itemA.current !== itemB.current) {
            return false;
          }
        }
      } else {
        return false;
      }
    }
    return true;
  }

  init(data) {
    this.width = this.frame.offsetWidth - margin.left - margin.right;
    this.width = this.width < 0 ? 0 : this.width;
    this.height = this.frame.offsetHeight - margin.top - margin.bottom;
    this.height = this.height < 0 ? 0 : this.height;
    this._x = x(this.width);
    this._y = y(this.height);
    this._x.domain(data.map(function(d) { return d.name; }));
    this._y.domain([0, d3.max(data, function(d) { return d.value; })]);
    this.mean = d3.mean(data, function(d) { return d.value; });
    this.meanY = this._y(this.mean);
  }

  initChart(data) {
    this.init(data);
    this.svg = d3.select(this.svgEl);
    this.chart = this.svg.append('g');
    this.barsGroup = this.chart.append('g');

    this.barGroups = this.barsGroup.selectAll('.bar-group')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr('transform', (d)=> { return 'translate(' + this._x(d.name) + ',' + 0 + ')'});

    this.barGroups.append('rect')
      .attr('class', 'bar')
      .on('mouseover', this.handleMouseOver)
      .on('mouseout', this.handleMouseOut)
      .attr('y', this.height)
      .attr('x', 0)
      .attr('height', 0);

    this.barGroups.append('polygon')
      .attr('class', 'bar-top')
      .attr('points', ()=> { return this.getBarTopPoints() })
      .attr('transform', (d)=> { return 'translate(0, ' + this.height + ')' });

    this.bottomAxis = this.chart.append('g')
        .attr('class', 'bottom-axis');

    this.leftAxis = this.chart.append('g')
      .attr('class', 'left-axis');

    this.grid = this.chart.append('g')
      .attr('class', 'grid');

    this.meanLine = this.chart.append('g').attr('class', 'mean');
    this.meanLine.append('line').attr('x1', 0).attr('x2', this.width);
    this.meanLine.append('text').attr('x', 3).attr('y', -3);
  }

  updateChart(data, duration = 750) {
    this.init(data);
    let yGrid = ()=> { return d3.axisLeft(this._y).ticks(3).tickSize(-this.width).tickFormat('') }

    this.svg
      .attr('width', this.width + margin.left + margin.right)
      .attr('height', this.height + margin.top + margin.bottom);

    this.chart
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // Update data for all bar groups
    this.barGroups = this.barsGroup.selectAll('.bar-group').data(data);

    // Remove old bar broups
    this.barGroups.exit().remove();

    // Enter new bar groups
    let newBarGroups = this.barGroups.enter()
      .append('g')
      .attr('class', 'bar-group');

    // Add bar to bar group
    newBarGroups.append('rect')
      .attr('class', 'bar')
      .on('mouseover', this.handleMouseOver)
      .on('mouseout', this.handleMouseOut)
      .attr('y', this.height)
      .attr('x', 0)
      .attr('height', 0);

    // Add bar top to the bar group
    newBarGroups.append('polygon')
      .attr('class', 'bar-top')
      .attr('transform', (d)=> { return 'translate(0, ' + this.height + ')' });

    // Update the position of all brargroups
    this.barGroups.merge(newBarGroups)
      .attr('transform', (d)=> { return 'translate(' + this._x(d.name) + ',' + 0 + ')'})
      .classed('current', (d)=> {
        return d.name === this.state.currentSkill.name;
      });

    // Update attributes of all bars
    this.barGroups.merge(newBarGroups).select('.bar')
      .attr('width', this._x.bandwidth())
      .attr('class', 'bar')
    .transition(d3.transition().duration(duration))
      .attr('y', (d)=> { return this._y(d.value) })
      .attr('height', (d)=> { return this.height - this._y(d.value) });

    // Update attributes of all bar tops
    this.barGroups.merge(newBarGroups).select('.bar-top')
      .attr('class', 'bar-top')
      .attr('points', ()=> { return this.getBarTopPoints() })
    .transition(d3.transition().duration(duration))
      .attr('transform', (d)=> { return 'translate(0, ' + this._y(d.value) + ')' });

    this.leftAxis
      .transition(d3.transition().duration(duration))
      .call(
        d3.axisLeft(this._y)
        .ticks(3)
        .tickFormat(d3.format('d'))
      );

    this.bottomAxis
      .transition(d3.transition().duration(duration))
      .call(d3.axisBottom(this._x))
      .attr('transform', 'translate(0,' + this.height + ')');

    this.grid
      .transition(d3.transition().duration(duration))
      .call(yGrid());

    this.meanLine
        .transition(d3.transition().duration(duration))
        .attr('transform', 'translate(0,' + this.meanY + ')');

    this.meanLine.select('text').text('Mean: ' + this.mean.toFixed(2));
  }

  getBarTopPoints = ()=> {
    let barWidth = this._x.bandwidth();
    return '0 0,' + barThickness + ' -' + barThickness + ',' + (barWidth + barThickness) + ' -' + barThickness + ',' + barWidth + ' 0';
  }

  handleMouseOver = (d)=> {
    this.setState({ selected: d });

    let popupWidth = this.popup.offsetWidth;
    let barWidth = this._x.bandwidth();
    let leftOffset = Math.round((barWidth - popupWidth) / 2);
    let left = this._x(d.name) + margin.left + leftOffset;
    let top = this._y(d.value);

    this.popup.style.left = left + 'px';
    this.popup.style.top = (top + margin.top - 60) + 'px';
    this.popup.style.opacity = 1;
  }

  handleMouseOut = (d)=> {
    this.popup.style.opacity = 0;
  }

  resize = ()=> {
    this.chart
      .transition()
      .duration(500)
      .style('opacity', 0)
      .remove()
      .on('end', ()=> {
        this.initChart(this.state.data || initialData);
        this.updateChart(this.state.data || initialData, 0);
      })
  }

  setCurrentInBars = (skillName)=> {
    this.barsGroup.selectAll('.bar-group')
      .classed('current', function(d) {
        return d.name === skillName;
      });
  }

  render() {
    return (
      <div className="bar-chart" ref={(el) => { this.frame = el; }}>

        <div className="bar-popup" ref={(el) => { this.popup = el; }}>
          <div className="row name">
            { this.state.selected.name }
          </div>
          <div className="row value">
            { this.state.selected.value }
          </div>
          <div className="tri"></div>
        </div>

        <svg ref={(el) => { this.svgEl = el; }}></svg>
      </div>
    );
  }
}

export default BarChart;



// WEBPACK FOOTER //
// ./src/BarChart/BarChart.js